import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Loader2, PlusCircle, StickyNote } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));

export default function ExtraChargesPanel({ job, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const charges = job.extra_charges || [];
  const total = Number(job.extra_charges_total || 0);
  const [form, setForm] = useState({ description: "", amount: "" });
  const [saving, setSaving] = useState(false);

  // Visibile a tutti chi vede il saldo (quindi dentro ForemanLock già sbloccato),
  // ma SOLO l'admin può aggiungere/rimuovere.
  if (charges.length === 0 && !isAdmin) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim() || !form.amount) {
      toast.error("Inserisci descrizione e importo");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/extra-charges`, {
        description: form.description.trim(),
        amount: Number(form.amount),
      });
      onChanged?.(data);
      setForm({ description: "", amount: "" });
      toast.success("Aggiunta registrata — sommata al saldo");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Rimuovere questa aggiunta?")) return;
    try {
      const { data } = await api.delete(`/jobs/${job.id}/extra-charges/${id}`);
      onChanged?.(data);
      toast.success("Aggiunta rimossa");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section
      className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-6"
      data-testid="extra-charges-panel"
    >
      <h3 className="font-display font-bold text-amber-900 flex items-center gap-2 mb-1">
        <PlusCircle className="w-4 h-4" /> Note di aggiunte & Euro in più
      </h3>
      <p className="text-xs text-amber-800/80 mb-4">
        Costi extra rilevati durante il lavoro (oggetti aggiunti, scatoloni, fermo, scale…). <b>Vengono sommati al saldo</b> che i dipendenti devono riscuotere dal cliente.
      </p>

      {charges.length > 0 && (
        <ul className="space-y-2 mb-4">
          {charges.map((c) => (
            <li
              key={c.id}
              className="bg-white border border-amber-200 rounded-xl px-3 py-2.5 flex items-center gap-3"
              data-testid={`extra-charge-${c.id}`}
            >
              <StickyNote className="w-4 h-4 text-amber-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{c.description}</div>
                <div className="text-[11px] text-slate-400 font-mono">
                  {c.created_by_name} · {c.created_at?.slice(0, 10)}
                </div>
              </div>
              <div className="font-mono font-bold text-emerald-700 tabular-nums">
                +{fmt(c.amount)}
              </div>
              {isAdmin && (
                <button
                  onClick={() => remove(c.id)}
                  className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600"
                  data-testid={`delete-extra-charge-${c.id}`}
                  title="Rimuovi"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {charges.length > 0 && (
        <div className="flex items-center justify-between bg-amber-100 border border-amber-300 rounded-xl px-4 py-2.5 mb-4">
          <div className="text-xs uppercase tracking-widest font-bold text-amber-900">
            Totale aggiunte
          </div>
          <div className="font-display text-xl font-extrabold text-amber-900 tabular-nums" data-testid="extra-charges-total">
            +{fmt(total)}
          </div>
        </div>
      )}

      {isAdmin && (
        <form
          onSubmit={submit}
          className="bg-white border border-amber-200 rounded-xl p-3 grid grid-cols-1 md:grid-cols-5 gap-2 items-end"
          data-testid="extra-charge-form"
        >
          <div className="md:col-span-3">
            <Label className="text-xs">Nota aggiunta</Label>
            <Textarea
              rows={1}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Es: 10 scatoloni libri aggiunti, fermo 1h"
              data-testid="extra-charge-description-input"
            />
          </div>
          <div>
            <Label className="text-xs">Euro in più</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="50"
              data-testid="extra-charge-amount-input"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="add-extra-charge-button"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Aggiungi
          </Button>
        </form>
      )}
    </section>
  );
}
